import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, type ReactNode, useEffect, useState } from 'react'
import { StatTile } from '../../components/charts'
import { Modal } from '../../components/Modal'
import { numero, shortTime } from '../../lib/format'
import { t } from '../../lib/i18n'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/meta')({ component: MetaAds })

/**
 * Pixel, Conversions API e a página-ponte das lives.
 *
 * Três assuntos e um registro, na ordem em que se configura: primeiro a conta
 * (sem ela nada sai), depois qual tag vira conversão, depois os links que vão
 * no botão de cada live. Os eventos ficam por último, como conferência.
 */
function MetaAds() {
  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-4xl px-5 pt-4 md:pt-8">
        <h1 className="font-display text-2xl text-ink">{t('Meta Ads')}</h1>
        <p className="mt-1 text-xs leading-relaxed text-ink-2">
          {t(
            'Manda para a Meta quem chegou pela live e quem depositou, para as campanhas aprenderem com quem converte de verdade.',
          )}
        </p>

        <ContaDaMeta />
        <Regras />
        <LinksDaPonte />
        <EventosRecentes />
      </div>
      <style>{`
        .campo {
          width: 100%;
          border-radius: 8px;
          border: 1px solid var(--line-strong);
          background: var(--surface);
          padding: 7px 10px;
          font-size: 13px;
          color: var(--ink);
        }
      `}</style>
    </div>
  )
}

function Cartao({
  title,
  description,
  acao,
  children,
}: {
  title: string
  description?: ReactNode
  acao?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-line bg-surface">
      <header className="flex items-start gap-3 border-b border-line bg-surface-2/50 px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-md font-semibold text-ink">{title}</h2>
          {description && (
            <p className="mt-0.5 text-xs leading-relaxed text-ink-2">{description}</p>
          )}
        </div>
        {acao}
      </header>
      <div className="p-4">{children}</div>
    </section>
  )
}

function Campo({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-ink-3">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-2xs leading-relaxed text-ink-3">{hint}</span>}
    </label>
  )
}

function Erro({ mensagem }: { mensagem: string | undefined }) {
  if (!mensagem) return null
  return (
    <p role="alert" className="text-xs text-danger">
      {t(mensagem)}
    </p>
  )
}

const botaoPrimario =
  'rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:bg-accent-ink disabled:opacity-50'
const botaoSecundario =
  'rounded-lg border border-line-strong px-3 py-2 text-sm text-ink-2 hover:bg-surface-2 disabled:opacity-50'

/** As chaves de campo que já existem nos contatos, para não digitar de memória. */
function SugestoesDeCampo({ id, campos }: { id: string; campos: string[] }) {
  return (
    <datalist id={id}>
      {campos.map((campo) => (
        <option key={campo} value={campo} />
      ))}
    </datalist>
  )
}

function ContaDaMeta() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const dados = useQuery(trpc.meta.config.queryOptions())
  const config = dados.data?.config

  const [pixelId, setPixelId] = useState('')
  const [token, setToken] = useState('')
  const [testEventCode, setTestEventCode] = useState('')
  const [ativo, setAtivo] = useState(true)
  const [enviarLead, setEnviarLead] = useState(true)
  const [campoTelefone, setCampoTelefone] = useState('')
  const [campoEmail, setCampoEmail] = useState('')

  // Preenche o formulário uma vez, quando a configuração chega. Depois disso o
  // texto é de quem está digitando, e uma nova leitura não pode apagá-lo.
  const [carregado, setCarregado] = useState(false)
  useEffect(() => {
    if (carregado || dados.isPending) return
    setPixelId(config?.pixelId ?? '')
    setTestEventCode(config?.testEventCode ?? '')
    setAtivo(config?.ativo ?? true)
    setEnviarLead(config?.enviarLead ?? true)
    setCampoTelefone(config?.campoTelefone ?? '')
    setCampoEmail(config?.campoEmail ?? '')
    setCarregado(true)
  }, [carregado, dados.isPending, config])

  const salvar = useMutation({
    ...trpc.meta.salvarConfig.mutationOptions(),
    onSuccess: () => {
      setToken('')
      queryClient.invalidateQueries({ queryKey: [['meta']] })
    },
  })
  const testar = useMutation(trpc.meta.testar.mutationOptions())

  function submit(event: FormEvent) {
    event.preventDefault()
    testar.reset()
    salvar.mutate({
      pixelId,
      ...(token.trim() ? { token: token.trim() } : {}),
      testEventCode: testEventCode.trim() || null,
      ativo,
      enviarLead,
      campoTelefone: campoTelefone.trim() || null,
      campoEmail: campoEmail.trim() || null,
    })
  }

  return (
    <Cartao
      title={t('Pixel e Conversions API')}
      description={t(
        'O Pixel roda na página-ponte; o token deixa o servidor mandar o depósito, que acontece longe do navegador.',
      )}
    >
      <form onSubmit={submit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo label={t('ID do Pixel')}>
            <input
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              required
              inputMode="numeric"
              pattern="\d{5,20}"
              placeholder="123456789012345"
              className="campo"
            />
          </Campo>
          <Campo
            label={t('Token da Conversions API')}
            hint={
              config?.temToken
                ? t('Gravado, termina em {final}. Deixe vazio para manter.', {
                    final: config.tokenHint ?? '',
                  })
                : t('Gerado no Gerenciador de Eventos, em Configurações do Pixel.')
            }
          >
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoComplete="off"
              placeholder={config?.temToken ? '••••••••' : 'EAAB…'}
              className="campo"
            />
          </Campo>
          <Campo
            label={t('Campo do telefone')}
            hint={t('Vai com hash, e é o que mais ajuda a Meta a reconhecer a pessoa.')}
          >
            <input
              value={campoTelefone}
              onChange={(e) => setCampoTelefone(e.target.value)}
              list="meta-campos"
              placeholder="telefone"
              className="campo"
            />
          </Campo>
          <Campo label={t('Campo do e-mail')} hint={t('Opcional.')}>
            <input
              value={campoEmail}
              onChange={(e) => setCampoEmail(e.target.value)}
              list="meta-campos"
              placeholder="email"
              className="campo"
            />
          </Campo>
          <Campo
            label={t('Código de teste')}
            hint={t(
              'Só enquanto confere na aba “Testar eventos”. Com ele preenchido, nada conta para as campanhas.',
            )}
          >
            <input
              value={testEventCode}
              onChange={(e) => setTestEventCode(e.target.value)}
              placeholder="TEST12345"
              className="campo"
            />
          </Campo>
        </div>
        <SugestoesDeCampo id="meta-campos" campos={dados.data?.campos ?? []} />

        {config?.testEventCode && (
          <p className="rounded-md border border-tag-amber/40 bg-tag-amber/10 px-3 py-2 text-xs text-ink">
            {t(
              'O código de teste está ligado: os eventos vão só para a aba de teste e não contam para as campanhas.',
            )}
          </p>
        )}

        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
            {t('Enviar eventos para a Meta')}
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={enviarLead}
              onChange={(e) => setEnviarLead(e.target.checked)}
            />
            {t('Mandar “Lead” quando o contato chega ao bot pela página-ponte')}
          </label>
        </div>

        <Erro mensagem={salvar.error?.message} />
        {testar.data && (
          <p className={`text-xs ${testar.data.ok ? 'text-st-resolv' : 'text-danger'}`}>
            {testar.data.ok
              ? t('A Meta recebeu o evento de teste. Confira na aba “Testar eventos”.')
              : t('A Meta recusou: {erro}', { erro: testar.data.erro })}
          </p>
        )}
        <Erro mensagem={testar.error?.message} />

        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={salvar.isPending} className={botaoPrimario}>
            {salvar.isPending ? t('Salvando…') : t('Salvar')}
          </button>
          {config && (
            <button
              type="button"
              disabled={testar.isPending}
              onClick={() => testar.mutate()}
              className={botaoSecundario}
            >
              {testar.isPending ? t('Testando…') : t('Testar conexão')}
            </button>
          )}
        </div>
      </form>
    </Cartao>
  )
}

/** Os eventos que fazem sentido para uma tag, com o rótulo de cada um. */
const EVENTOS = [
  { valor: 'Purchase', rotulo: 'Compra (Purchase)' },
  { valor: 'Lead', rotulo: 'Lead' },
  { valor: 'CompleteRegistration', rotulo: 'Cadastro concluído (CompleteRegistration)' },
  { valor: 'Contact', rotulo: 'Contato (Contact)' },
  { valor: 'Subscribe', rotulo: 'Assinatura (Subscribe)' },
] as const

type Evento = (typeof EVENTOS)[number]['valor']

interface Regra {
  id: string
  tagId: string
  evento: string
  campoValor: string | null
  moeda: string
}

function Regras() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const regras = useQuery(trpc.meta.regras.queryOptions())
  const [editando, setEditando] = useState<Regra | 'nova' | null>(null)
  const [confirmando, setConfirmando] = useState<string | null>(null)

  const apagar = useMutation({
    ...trpc.meta.apagarRegra.mutationOptions(),
    onSuccess: () => {
      setConfirmando(null)
      queryClient.invalidateQueries({ queryKey: [['meta']] })
    },
  })

  return (
    <Cartao
      title={t('Tag que vira conversão')}
      description={t(
        'Quando a tag é marcada num contato, o evento sai para a Meta — uma vez por pessoa, mesmo que a tag seja tirada e marcada de novo.',
      )}
      acao={
        <button type="button" onClick={() => setEditando('nova')} className={botaoSecundario}>
          {t('Nova regra')}
        </button>
      }
    >
      {regras.data?.length === 0 && (
        <p className="text-xs text-ink-3">
          {t('Nenhuma regra ainda. Crie uma para a tag de primeiro depósito.')}
        </p>
      )}
      <ul className="divide-y divide-line">
        {regras.data?.map((regra) => (
          <li key={regra.id} className="flex flex-wrap items-center gap-2 py-2.5">
            <span
              className="rounded-full border px-2 py-0.5 text-xs font-semibold"
              style={{
                color: regra.tagColor,
                borderColor: `color-mix(in srgb, ${regra.tagColor} 40%, transparent)`,
              }}
            >
              {regra.tagLabel}
            </span>
            <span className="text-xs text-ink-3">→</span>
            <span className="text-sm font-semibold text-ink">{regra.evento}</span>
            <span className="text-xs text-ink-2">
              {regra.campoValor
                ? t('valor do campo {campo}, em {moeda}', {
                    campo: regra.campoValor,
                    moeda: regra.moeda,
                  })
                : t('sem valor')}
            </span>
            <span className="ml-auto flex gap-1">
              <button
                type="button"
                onClick={() => setEditando(regra)}
                className="rounded px-2 py-1 text-2xs text-ink-2 hover:bg-surface-2"
              >
                {t('Editar')}
              </button>
              {confirmando === regra.id ? (
                <button
                  type="button"
                  onClick={() => apagar.mutate({ id: regra.id })}
                  className="rounded px-2 py-1 text-2xs font-semibold text-danger hover:bg-surface-2"
                >
                  {t('Confirmar')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmando(regra.id)}
                  className="rounded px-2 py-1 text-2xs text-ink-2 hover:bg-surface-2"
                >
                  {t('Apagar')}
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>

      {editando && (
        <Modal
          title={editando === 'nova' ? t('Nova regra') : t('Editar regra')}
          onClose={() => setEditando(null)}
        >
          <FormularioDeRegra
            regra={editando === 'nova' ? null : editando}
            onPronto={() => setEditando(null)}
          />
        </Modal>
      )}
    </Cartao>
  )
}

function FormularioDeRegra({ regra, onPronto }: { regra: Regra | null; onPronto: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const tags = useQuery(trpc.lead.tags.queryOptions())
  const dados = useQuery(trpc.meta.config.queryOptions())

  const [tagId, setTagId] = useState(regra?.tagId ?? '')
  const [evento, setEvento] = useState<Evento>((regra?.evento as Evento) ?? 'Purchase')
  const [campoValor, setCampoValor] = useState(regra?.campoValor ?? '')
  const [moeda, setMoeda] = useState(regra?.moeda ?? 'CLP')

  const salvar = useMutation({
    ...trpc.meta.salvarRegra.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [['meta']] })
      onPronto()
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    salvar.mutate({
      ...(regra ? { id: regra.id } : {}),
      tagId,
      evento,
      campoValor: campoValor.trim() || null,
      moeda,
    })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo label={t('Tag')}>
          <select
            value={tagId}
            onChange={(e) => setTagId(e.target.value)}
            required
            className="campo"
          >
            <option value="">{t('Selecione…')}</option>
            {[...(tags.data ?? [])]
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.label}
                </option>
              ))}
          </select>
        </Campo>
        <Campo label={t('Evento')}>
          <select
            value={evento}
            onChange={(e) => setEvento(e.target.value as Evento)}
            className="campo"
          >
            {EVENTOS.map((opcao) => (
              <option key={opcao.valor} value={opcao.valor}>
                {t(opcao.rotulo)}
              </option>
            ))}
          </select>
        </Campo>
        <Campo
          label={t('Campo do valor')}
          hint={t(
            'Preenchido, a tela pede o valor antes de deixar marcar a tag. Vazio, o evento vai sem valor.',
          )}
        >
          <input
            value={campoValor}
            onChange={(e) => setCampoValor(e.target.value)}
            list="meta-campos-regra"
            placeholder="valor_deposito"
            className="campo"
          />
        </Campo>
        <Campo label={t('Moeda')}>
          <input
            value={moeda}
            onChange={(e) => setMoeda(e.target.value.toUpperCase())}
            required
            maxLength={3}
            pattern="[A-Za-z]{3}"
            className="campo"
          />
        </Campo>
      </div>
      <SugestoesDeCampo id="meta-campos-regra" campos={dados.data?.campos ?? []} />
      <Erro mensagem={salvar.error?.message} />
      <button type="submit" disabled={salvar.isPending || !tagId} className={botaoPrimario}>
        {salvar.isPending ? t('Salvando…') : t('Salvar')}
      </button>
    </form>
  )
}

interface Link {
  id: string
  slug: string
  rotulo: string
  channelId: string
  chaveStart: string
  ativo: boolean
}

function LinksDaPonte() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const links = useQuery(trpc.meta.links.queryOptions())
  const dados = useQuery(trpc.meta.config.queryOptions())
  const base = dados.data?.baseDaPonte ?? ''
  const [editando, setEditando] = useState<Link | 'novo' | null>(null)
  const [confirmando, setConfirmando] = useState<string | null>(null)

  const apagar = useMutation({
    ...trpc.meta.apagarLink.mutationOptions(),
    onSuccess: () => {
      setConfirmando(null)
      queryClient.invalidateQueries({ queryKey: [['meta']] })
    },
  })

  const linhas = links.data ?? []
  const total = (campo: 'cliques' | 'leads' | 'conversoes' | 'valor') =>
    linhas.reduce((soma, linha) => soma + Number(linha[campo] ?? 0), 0)

  return (
    <Cartao
      title={t('Página-ponte das lives')}
      description={t(
        'Vai no botão da live no lugar do t.me. Dispara o Pixel e segue para o bot em menos de dois segundos, levando junto quem clicou.',
      )}
      acao={
        <button type="button" onClick={() => setEditando('novo')} className={botaoSecundario}>
          {t('Novo link')}
        </button>
      }
    >
      {linhas.length > 0 && (
        <div className="mb-4 grid gap-3 sm:grid-cols-4">
          <StatTile label={t('Cliques')} value={numero(total('cliques'))} />
          <StatTile label={t('Chegaram ao bot')} value={numero(total('leads'))} />
          <StatTile label={t('Conversões')} value={numero(total('conversoes'))} />
          <StatTile label={t('Valor convertido')} value={numero(total('valor'))} />
        </div>
      )}

      {!links.isPending && linhas.length === 0 && (
        <p className="text-xs text-ink-3">
          {t('Nenhum link ainda. Crie um por origem: cada live, a bio, os stories.')}
        </p>
      )}

      <ul className="divide-y divide-line">
        {linhas.map((link) => (
          <li key={link.id} className="py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-ink">{link.rotulo}</span>
              {!link.ativo && (
                <span className="rounded-full bg-surface-2 px-2 py-0.5 text-2xs text-ink-3">
                  {t('desligado')}
                </span>
              )}
              <span className="text-xs text-ink-3">
                {t('{conta} · chave {chave}', { conta: link.canal, chave: link.chaveStart })}
              </span>
              <span className="ml-auto flex gap-1">
                <button
                  type="button"
                  onClick={() => setEditando(link)}
                  className="rounded px-2 py-1 text-2xs text-ink-2 hover:bg-surface-2"
                >
                  {t('Editar')}
                </button>
                {confirmando === link.id ? (
                  <button
                    type="button"
                    onClick={() => apagar.mutate({ id: link.id })}
                    className="rounded px-2 py-1 text-2xs font-semibold text-danger hover:bg-surface-2"
                  >
                    {t('Confirmar')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmando(link.id)}
                    className="rounded px-2 py-1 text-2xs text-ink-2 hover:bg-surface-2"
                  >
                    {t('Apagar')}
                  </button>
                )}
              </span>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-surface-2 px-2 py-1 text-2xs text-ink-2">
                {base}
                {link.slug}
              </code>
              <Copiar texto={`${base}${link.slug}`} />
            </div>
            <p className="mt-1.5 text-2xs text-ink-3">
              {t(
                '{cliques} cliques · {leads} no bot · {conversoes} conversões · {valor} em valor',
                {
                  cliques: numero(link.cliques),
                  leads: numero(link.leads),
                  conversoes: numero(link.conversoes),
                  valor: numero(link.valor),
                },
              )}
            </p>
          </li>
        ))}
      </ul>

      {editando && (
        <Modal
          title={editando === 'novo' ? t('Novo link') : t('Editar link')}
          description={t(
            'A chave é a mesma do /start que a automação da live já espera — o fluxo continua igual.',
          )}
          onClose={() => setEditando(null)}
        >
          <FormularioDeLink
            link={editando === 'novo' ? null : editando}
            base={base}
            onPronto={() => setEditando(null)}
          />
        </Modal>
      )}
    </Cartao>
  )
}

function Copiar({ texto }: { texto: string }) {
  const [copiado, setCopiado] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(texto)
        setCopiado(true)
        setTimeout(() => setCopiado(false), 1200)
      }}
      className="shrink-0 rounded border border-line px-2 py-1 text-2xs text-ink-2 hover:bg-surface-2"
    >
      {copiado ? t('Copiado') : t('Copiar')}
    </button>
  )
}

function FormularioDeLink({
  link,
  base,
  onPronto,
}: {
  link: Link | null
  base: string
  onPronto: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const canais = useQuery(trpc.channel.list.queryOptions())
  const bots = (canais.data ?? []).filter((canal) => canal.type === 'bot')

  const [rotulo, setRotulo] = useState(link?.rotulo ?? '')
  const [slug, setSlug] = useState(link?.slug ?? '')
  const [channelId, setChannelId] = useState(link?.channelId ?? '')
  const [chaveStart, setChaveStart] = useState(link?.chaveStart ?? '')
  const [ativo, setAtivo] = useState(link?.ativo ?? true)

  // Com uma conta só, não há o que escolher.
  useEffect(() => {
    if (!channelId && bots.length === 1 && bots[0]) setChannelId(bots[0].id)
  }, [channelId, bots])

  const salvar = useMutation({
    ...trpc.meta.salvarLink.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [['meta']] })
      onPronto()
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    salvar.mutate({
      ...(link ? { id: link.id } : {}),
      rotulo,
      slug,
      channelId,
      chaveStart,
      ativo,
    })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo label={t('Nome')} hint={t('Como aparece no CRM')}>
          <input
            value={rotulo}
            onChange={(e) => setRotulo(e.target.value)}
            required
            placeholder="Live Kako"
            className="campo"
          />
        </Campo>
        <Campo label={t('Endereço')} hint={`${base}${slug || '…'}`}>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            required
            pattern="[a-z0-9][a-z0-9\-]{1,39}"
            placeholder="live-kako"
            className="campo"
          />
        </Campo>
        <Campo label={t('Bot')}>
          <select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            required
            className="campo"
          >
            <option value="">{t('Selecione…')}</option>
            {bots.map((canal) => (
              <option key={canal.id} value={canal.id}>
                {canal.name}
              </option>
            ))}
          </select>
        </Campo>
        <Campo label={t('Chave do /start')} hint={t('Letras, números, _ e - (até 53)')}>
          <input
            value={chaveStart}
            onChange={(e) => setChaveStart(e.target.value)}
            required
            pattern="[A-Za-z0-9_\-]{1,53}"
            placeholder="live_kako"
            className="campo"
          />
        </Campo>
      </div>
      <label className="flex items-center gap-2 text-sm text-ink">
        <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
        {t('Link ligado')}
      </label>
      <Erro mensagem={salvar.error?.message} />
      <button type="submit" disabled={salvar.isPending || !channelId} className={botaoPrimario}>
        {salvar.isPending ? t('Salvando…') : t('Salvar')}
      </button>
    </form>
  )
}

/** Rótulo do status, em português; traduzido na hora de desenhar. */
const STATUS: Record<string, { rotulo: string; cor: string }> = {
  pending: { rotulo: 'na fila', cor: 'text-ink-3' },
  sending: { rotulo: 'enviando', cor: 'text-ink-3' },
  sent: { rotulo: 'enviado', cor: 'text-st-resolv' },
  failed: { rotulo: 'falhou', cor: 'text-danger' },
}

function EventosRecentes() {
  const trpc = useTRPC()
  const eventos = useQuery({ ...trpc.meta.eventos.queryOptions(), refetchInterval: 30_000 })

  return (
    <Cartao
      title={t('Eventos recentes')}
      description={t('Os últimos cinquenta mandados à Meta, com o motivo de quem falhou.')}
    >
      {eventos.data?.length === 0 && (
        <p className="text-xs text-ink-3">{t('Nenhum evento ainda.')}</p>
      )}
      <ul className="divide-y divide-line">
        {eventos.data?.map((evento) => {
          const status = STATUS[evento.status] ?? { rotulo: evento.status, cor: 'text-ink-3' }
          const nome =
            [evento.nome, evento.sobrenome].filter(Boolean).join(' ') ||
            (evento.username ? `@${evento.username}` : t('Sem nome'))
          return (
            <li key={evento.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2">
              <span className="w-24 shrink-0 text-2xs text-ink-3">
                {shortTime(evento.ocorridoEm)}
              </span>
              <span className="text-sm font-semibold text-ink">{evento.evento}</span>
              <span className="min-w-0 truncate text-sm text-ink-2">{nome}</span>
              {evento.valor !== null && (
                <span className="text-xs text-ink-2">
                  {numero(evento.valor)} {evento.moeda}
                </span>
              )}
              {evento.origem && <span className="text-2xs text-ink-3">{evento.origem}</span>}
              <span className={`ml-auto text-2xs font-semibold ${status.cor}`}>
                {t(status.rotulo)}
              </span>
              {evento.status === 'failed' && evento.ultimoErro && (
                <span className="w-full text-2xs text-danger">{t(evento.ultimoErro)}</span>
              )}
            </li>
          )
        })}
      </ul>
    </Cartao>
  )
}
